import { useCallback, useMemo } from "react";
import { createColumnHelper, useReactTable, getCoreRowModel, getSortedRowModel } from "@tanstack/react-table";
import { type BusinessUser, type BusinessSubscription } from "lens-platform-sdk";
import { action } from "@k8slens/lds-icons";

import { type ActiveSeat, isActiveSeat } from "src/services/getSubscriptionSeatState";
import { useLensPlatformClient } from "src/hooks/useLensPlatformClient";
import { useTrackedTableSort } from "src/hooks/useTrackedTableSort";

import { AvatarTableCell } from "src/components/TableCells/AvatarTableCell";
import DateCell, { dateColDef, dateColSortingFn } from "src/components/TableCells/DateCell";
import Table from "src/components/Table/Table";
import { ContextMenuItem } from "src/components/ContextMenu/ContextMenu";
import ContextMenuCell, { contextMenuColDef } from "src/components/TableCells/ContextMenuCell";
import { subscriptionColDef } from "src/components/TableCells/SubscriptionCell";

import styles from "./ActiveSeatTable.module.css";

const { DeleteIcon } = action;

const activeSeatTableColumnHelper = createColumnHelper<ActiveSeat>();

interface Props {
  businessSubscriptions: BusinessSubscription[];
  businessUsers: BusinessUser[];
  onUnassignSeat?: (id: string) => void;
}

const caption = "Active seats";

/**
 * The table to display active (used and not deactivated) seats
 */
const ActiveSeatTable = ({ businessSubscriptions, businessUsers, onUnassignSeat }: Props) => {
  const platformClient = useLensPlatformClient();
  const getLastAccessForUser = useCallback(
    (userId: string | undefined) => (userId ? businessUsers.find(({ id }) => id === userId)?.lastAccess : undefined),
    [businessUsers],
  );
  const activeSeatTableColumns = useMemo(
    () => [
      activeSeatTableColumnHelper.accessor("user", {
        header: () => <span>Lens ID</span>,
        cell: ({ getValue }) => {
          const user = getValue();

          if (user) {
            const { username, email } = user;
            const fullname = platformClient.user.getUserFullName(user);

            return <AvatarTableCell name={username} subtitle={fullname} email={email} />;
          }

          return (
            <div className={styles.noUser}>
              <div>N/A</div>
            </div>
          );
        },
        meta: { primary: true },
      }),
      activeSeatTableColumnHelper.accessor("subscription", { ...subscriptionColDef }),
      activeSeatTableColumnHelper.accessor("activatedAt", {
        ...dateColDef,
        meta: { ...dateColDef.meta, text: "tertiary" },
        header: () => <span>Activation Date</span>,
      }),
      activeSeatTableColumnHelper.accessor("user", {
        ...dateColDef,
        id: "lastSeen",
        meta: { ...dateColDef.meta, text: "tertiary" },
        header: () => <span>Last Seen</span>,
        sortingFn: (row1, row2) => {
          const firstLastAccess = getLastAccessForUser(row1.original.user?.id);
          const secondLastAccess = getLastAccessForUser(row2.original.user?.id);

          return dateColSortingFn<{ lastSeen: unknown }>(
            { original: { lastSeen: firstLastAccess } },
            { original: { lastSeen: secondLastAccess } },
            "lastSeen",
          );
        },
        sortDescFirst: false,
        cell: ({ getValue }) => {
          const userId = getValue()?.id;

          return <DateCell getValue={() => getLastAccessForUser(userId)} noDataText="N/A" />;
        },
      }),
      activeSeatTableColumnHelper.accessor("offline", {
        header: () => <span>Offline</span>,
        cell: ({ getValue }) => (
          <div className={styles.offlineContainer}>
            {getValue() && <span title="Seat used offline" className={styles.offline} />}
          </div>
        ),
      }),
      activeSeatTableColumnHelper.accessor("id", {
        ...contextMenuColDef,
        cell: ({ getValue }) => (
          <ContextMenuCell toggleButtonProps={{ title: "Toggle context menu" }}>
            <ContextMenuItem
              label="Unassign"
              icon={DeleteIcon}
              onClick={() => onUnassignSeat && onUnassignSeat(getValue())}
            />
          </ContextMenuCell>
        ),
      }),
    ],
    [onUnassignSeat, platformClient.user, getLastAccessForUser],
  );

  const activeSeats = useMemo<ActiveSeat[]>(() => {
    const useSeats = businessSubscriptions
      .map(({ usedSeats }) => usedSeats)
      .flat()
      .filter(isActiveSeat);

    return useSeats.map((usedSeat) => ({
      ...usedSeat,
      subscription: businessSubscriptions.find(({ id }) => id === usedSeat.subscriptionId),
    }));
  }, [businessSubscriptions]);

  const { sorting, onSortingChange } = useTrackedTableSort({ table: caption });

  const activeTable = useReactTable({
    data: activeSeats,
    columns: activeSeatTableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    onSortingChange,
  });
  const activeTableRowModel = activeTable.getRowModel();
  const activeTableRows = useMemo(() => activeTableRowModel.rows, [activeTableRowModel]);

  return (
    <Table<ActiveSeat>
      caption={caption}
      rows={activeTableRows}
      columns={activeTable.getAllColumns()}
      headerGroups={activeTable.getHeaderGroups()}
      rowProps={{
        cellClassName: styles.tableCell,
      }}
      noDataText="No active seats on your Lens Business ID!"
    />
  );
};

export default ActiveSeatTable;
